import { isEmpty } from 'lodash';
import he from 'he';
import i18n from '@fiverr/i18n/singleton';
import axios from 'axios';
import { howLongAgo } from '@fiverr-private/futile';
import pathfinder from '@fiverr-private/pathfinder';
import type { LazyJobsTbdSdk, jtbdDismissArgs } from '@fiverr-private/jtbd_sdk';
import { logger } from '@fiverr-private/obs';
import { statsFunctionWrapper } from '@fiverr-private/homepage_utils';
import { ROLLOUTS } from '@fiverr-private/homepage_common_types';
import { Conversation } from '../types/conversations';
import { DEFAULT_ICON_URL } from './constants';

const SECONDS_TO_MILLISECONDS = 1000;
const TTL_7_DAYS = 7;

export const UnreadMessagesCardImpl = {
    getJobInfo: async ({ externalServicesInfo, rollouts, context }) => {
        try {
            const fromKimera = rollouts[ROLLOUTS.PANTHERS_HOMEPAGE_UNREAD_COUNTERS_FROM_KIMERA];
            const url = fromKimera
                ? `${externalServicesInfo.inboxKimera.url}/conversations/unread_counters/${context.userId}?filter=unread`
                : `${externalServicesInfo.inboxPhoenix.url}/inbox/counters`;
            const statsLabel = `jtbd.unread_messages.inbox_counters.${fromKimera ? 'kimera' : 'phoenix'}`;
            const fetcher = () => axios.get(url);
            const { data } = await statsFunctionWrapper(fetcher, statsLabel)();

            const unreadCount = fromKimera ? data?.count : data?.unread;
            const shouldDisplayJob = !isEmpty(data) && unreadCount > 0;

            return { shouldDisplayJob };
        } catch (error: any) {
            logger.error(error, { description: 'Error fetching number of unread messages' });

            return {
                shouldDisplayJob: false,
            };
        }
    },
    loadJobData: async ({ context, jtbdDismissInfo }) => {
        const unreadMsgsResult = await axios.get('/tailored_homepage/jtbd/unread_messages');

        if (!unreadMsgsResult) {
            return Promise.reject();
        }

        const conversations: Conversation[] = filterDismissedConversations(
            unreadMsgsResult.data,
            jtbdDismissInfo?.args
        );

        const firstConversation = conversations.slice(0, 1);

        return {
            headerText: i18n.t('jobs_tbd.unread_messages.header_text', { $scope: context.locale }),
            isEmphasizedCard: true,
            dismissInfo: {
                isDismissible: true,
                jtbdTTL: TTL_7_DAYS,
                itemId: firstConversation[0].id,
                updatedAt: firstConversation[0].updatedAt,
            },
            jobPages: firstConversation.map((conversation) => ({
                iconUrl: conversation.recipientUser.profileImageUrl || DEFAULT_ICON_URL,
                title: i18n.t('jobs_tbd.unread_messages.content_title.success', {
                    $scope: context.locale,
                    sender_name: conversation.recipientUser.fullName || conversation.recipientUser.name,
                }),
                sideNote: howLongAgo(new Date(conversation.updatedAt * SECONDS_TO_MILLISECONDS), {
                    prefix: 'jobs_tbd.timestamp.',
                    locale: context.locale,
                }),
                subtitle: he.decode(conversation.lastMessageBody),
                ctaText: i18n.t('jobs_tbd.unread_messages.cta', { $scope: context.locale }),
                sellerData: {
                    username: conversation.recipientUser.name,
                    isOnline: conversation.recipientUser.isOnline,
                    hasAvatar: !!conversation.recipientUser.profileImageUrl,
                },
                biEnrichment: {
                    inboxMessage: {
                        recipientId: Number(conversation.recipientUser.id),
                        conversationId: conversation.id,
                    },
                },
            })),
            clickAction: {
                type: 'link',
                href: pathfinder('inbox_conversation_page', {
                    username: conversations[0].recipientUser.name,
                }),
            },
        };
    },
    loadFallbackJobData: ({ context }) => ({
        headerText: i18n.t('jobs_tbd.unread_messages.header_text', { $scope: context.locale }),
        isEmphasizedCard: true,
        dismissInfo: {
            isDismissible: false,
        },
        jobPages: [
            {
                iconUrl: DEFAULT_ICON_URL,
                title: i18n.t('jobs_tbd.unread_messages.content_title.fallback', { $scope: context.locale }),
                subtitle: i18n.t('jobs_tbd.unread_messages.subtitle', { $scope: context.locale }),
            },
        ],
        clickAction: {
            type: 'link',
            href: pathfinder('inbox_home_page'),
        },
    }),
} satisfies LazyJobsTbdSdk;

const isConversationDismissed = (conversation: Conversation, dismissedMap: Map<string, number>) => {
    const dismissedConversationUpdatedAt = dismissedMap.get(conversation.id);

    if (dismissedConversationUpdatedAt) {
        if (conversation.updatedAt > dismissedConversationUpdatedAt) {
            void axios.delete('/tailored_homepage/dismiss_jtbd', {
                data: {
                    jtbdType: 'unread_messages',
                    itemId: conversation.id,
                    updatedAt: dismissedConversationUpdatedAt,
                },
            });

            return true;
        } else {
            return false;
        }
    }

    return true;
};

const filterDismissedConversations = (
    conversations: Conversation[],
    jtbdDismissArgs: jtbdDismissArgs[] | undefined
): Conversation[] => {
    if (!jtbdDismissArgs) {
        return conversations;
    }
    const dismissedMap = new Map(jtbdDismissArgs.map(([id, updatedAt]) => [id, Number(updatedAt)]));

    return conversations.filter((conversation) => isConversationDismissed(conversation, dismissedMap));
};
